import React, { useState, useRef, useCallback } from 'react';
import { Box, Tab, Tabs as MaterialTabs } from '@mui/material';

import { TabsProps, TextColorProps } from './Tabs.props';
import { a11yProps } from './Tabs.utils';
import { COLORS } from '../../../configs/colors';
import { TabPanel } from '../../atoms/TabPanel';

export const Tabs: React.FC<TabsProps> = ({
  name,
  tabTitles,
  children,
  textColor = TextColorProps.Primary,
  isCustomColor = false,
  TabIndicatorProps,
  onTabChange,
  variant,
  scrollButtons,
  tabSx,
  swipeable = false,
  keepMounted = false,
}) => {
  const [value, setValue] = useState(0);
  const startX = useRef(0);
  const filteredChildren = children.filter(Boolean);
  const tabCount = filteredChildren.length;

  const changeTab = useCallback(
    (newVal: number) => {
      setValue(newVal);
      onTabChange && onTabChange(tabTitles[newVal]);
    },
    [onTabChange, tabTitles]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const dx = e.changedTouches[0].clientX - startX.current;
      if (Math.abs(dx) < 50) return;
      if (dx < 0 && value < tabCount - 1) changeTab(value + 1);
      if (dx > 0 && value > 0) changeTab(value - 1);
    },
    [value, tabCount, changeTab]
  );

  const swipeHandlers = swipeable
    ? { onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd }
    : {};

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <MaterialTabs
          textColor={textColor}
          TabIndicatorProps={TabIndicatorProps}
          value={value}
          onChange={(_, val) => changeTab(val)}
          aria-label={`${name}-tabs-panel`}
          variant={variant}
          scrollButtons={scrollButtons}
        >
          {tabTitles.map((title, index) => (
            <Tab
              sx={{
                backgroundColor:
                  isCustomColor && index === value ? COLORS.LightGreen : COLORS.White,
                ...tabSx,
              }}
              key={index}
              label={title}
              {...a11yProps(name, index)}
            />
          ))}
        </MaterialTabs>
      </Box>
      <Box {...swipeHandlers}>
        {filteredChildren.map((node, index) => (
          <TabPanel key={index} value={value} index={index} keepMounted={keepMounted}>
            {node}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};
